import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useBranch } from "../../../contexts/BranchContext";
import AddEditClassModal from "./AddEditClassModal";
import EnrollmentPanel from "./EnrollmentPanel";
import AttendanceModal from "./AttendanceModal";

const CLASS_TYPE_COLORS = {
  Yoga: { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200", dot: "bg-purple-500" },
  HIIT: { bg: "bg-red-100", text: "text-red-700", border: "border-red-200", dot: "bg-red-500" },
  Strength: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-200", dot: "bg-blue-500" },
  Cardio: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200", dot: "bg-orange-500" },
  Pilates: { bg: "bg-pink-100", text: "text-pink-700", border: "border-pink-200", dot: "bg-pink-500" },
  Zumba: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-200", dot: "bg-yellow-500" },
  CrossFit: { bg: "bg-green-100", text: "text-green-700", border: "border-green-200", dot: "bg-green-500" },
  Spinning: { bg: "bg-cyan-100", text: "text-cyan-700", border: "border-cyan-200", dot: "bg-cyan-500" },
  Boxing: { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-200", dot: "bg-gray-500" },
  General: { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-200", dot: "bg-indigo-500" },
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 16 }, (_, i) => i + 6); // 6am to 9pm

function getWeekDates(baseDate) {
  const d = new Date(baseDate);
  const day = d?.getDay();
  const diff = d?.getDate() - day;
  const sunday = new Date(d.setDate(diff));
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(sunday);
    date?.setDate(sunday?.getDate() + i);
    return date;
  });
}

function formatHour(h) {
  if (h === 0) return "12am";
  if (h < 12) return `${h}am`;
  if (h === 12) return "12pm";
  return `${h - 12}pm`;
}

function formatDate(date) {
  return date?.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function isClassActive(cls) {
  const now = new Date();
  const classDate = new Date(cls?.start_date);
  const [sh, sm] = (cls?.start_time || "00:00")?.split(":")?.map(Number);
  const [eh, em] = (cls?.end_time || "00:00")?.split(":")?.map(Number);
  const classStart = new Date(classDate);
  classStart?.setHours(sh, sm, 0, 0);
  const classEnd = new Date(classDate);
  classEnd?.setHours(eh, em, 0, 0);
  return now >= classStart || now >= classEnd;
}

function getClassesForDayHour(classes, weekDates, dayIdx, hour) {
  const date = weekDates?.[dayIdx];
  if (!date) return [];
  const dateStr = date?.toISOString()?.split("T")?.[0];
  return classes?.filter(cls => {
    if (cls?.start_date !== dateStr) {
      // Check recurrence
      const clsDate = new Date(cls?.start_date);
      if (cls?.recurrence === "daily") {
        if (clsDate > date) return false;
      } else if (cls?.recurrence === "weekly") {
        if (clsDate > date) return false;
        if (clsDate?.getDay() !== date?.getDay()) return false;
      } else {
        return false;
      }
    }
    const [h] = (cls?.start_time || "00:00")?.split(":")?.map(Number);
    return h === hour;
  });
}

function ClassCard({ cls, onEdit, onEnroll, onAttendance, onTrainerChange, trainers }) {
  const typeColor = CLASS_TYPE_COLORS?.[cls?.class_type] || CLASS_TYPE_COLORS?.General;
  const enrolledCount = cls?.enrolled_count || 0;
  const attendedCount = cls?.attended_count || 0;
  const isFull = enrolledCount >= cls?.max_capacity;
  const canMarkAttendance = isClassActive(cls);
  const [showTrainerDropdown, setShowTrainerDropdown] = useState(false);
  const [changingTrainer, setChangingTrainer] = useState(false);

  const handleTrainerChange = async (trainerId) => {
    setChangingTrainer(true);
    await onTrainerChange?.(cls?.id, trainerId);
    setChangingTrainer(false);
    setShowTrainerDropdown(false);
  };

  return (
    <div className={`rounded-lg border p-2 text-xs cursor-pointer hover:shadow-md transition-all group ${typeColor?.bg} ${typeColor?.border}`}>
      <div className="flex items-start justify-between gap-1 mb-1">
        <p className={`font-semibold truncate ${typeColor?.text} leading-tight`}>{cls?.name}</p>
        <button
          onClick={(e) => { e?.stopPropagation(); onEdit?.(cls); }}
          className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-white/60 transition-all flex-shrink-0"
        >
          <Icon name="Pencil" size={10} color="currentColor" />
        </button>
      </div>
      <p className={`${typeColor?.text} opacity-70 truncate mb-1`}>
        {cls?.start_time?.slice(0, 5)} – {cls?.end_time?.slice(0, 5)}
      </p>
      {/* Trainer */}
      <div className="relative mb-1">
        <button
          onClick={(e) => { e?.stopPropagation(); setShowTrainerDropdown(!showTrainerDropdown); }}
          className={`flex items-center gap-1 ${typeColor?.text} opacity-80 hover:opacity-100 transition-opacity`}
          title="Change trainer"
        >
          <Icon name="User" size={9} color="currentColor" />
          <span className="truncate max-w-[80px]">{cls?.trainer_name || "No trainer"}</span>
          <Icon name="ChevronDown" size={9} color="currentColor" />
        </button>
        {showTrainerDropdown && (
          <div className="absolute top-full left-0 z-20 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-1">
            <button
              onClick={(e) => { e?.stopPropagation(); handleTrainerChange(null); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-600"
            >
              No Trainer
            </button>
            {trainers?.map(t => (
              <button
                key={t?.id}
                onClick={(e) => { e?.stopPropagation(); handleTrainerChange(t?.id); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-700"
              >
                {t?.full_name}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Stats row */}
      <div className="flex items-center justify-between gap-1">
        <span className={`${isFull ? "text-red-600 font-semibold" : typeColor?.text} opacity-80`}>
          {enrolledCount}/{cls?.max_capacity}
        </span>
        {attendedCount > 0 && (
          <span className="bg-green-100 text-green-700 px-1 py-0.5 rounded text-xs font-medium">
            {attendedCount}/{enrolledCount} ✓
          </span>
        )}
      </div>
      {/* Action buttons */}
      <div className="flex gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e?.stopPropagation(); onEnroll?.(cls); }}
          className="flex-1 flex items-center justify-center gap-0.5 py-1 bg-white/70 hover:bg-white rounded text-xs font-medium text-gray-700 transition-colors"
        >
          <Icon name="Users" size={9} color="currentColor" />
          <span>Enroll</span>
        </button>
        {canMarkAttendance && (
          <button
            onClick={(e) => { e?.stopPropagation(); onAttendance?.(cls); }}
            className="flex-1 flex items-center justify-center gap-0.5 py-1 bg-green-500 hover:bg-green-600 rounded text-xs font-medium text-white transition-colors"
          >
            <Icon name="ClipboardCheck" size={9} color="white" />
            <span>Attend</span>
          </button>
        )}
      </div>
    </div>
  );
}

export default function ClassesManager() {
  useAuth();
  const { selectedBranchId } = useBranch();
  const [classes, setClasses] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("week");
  const [currentWeekBase, setCurrentWeekBase] = useState(new Date());
  const [weekDates, setWeekDates] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [enrollmentClass, setEnrollmentClass] = useState(null);
  const [attendanceClass, setAttendanceClass] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    setWeekDates(getWeekDates(currentWeekBase));
  }, [currentWeekBase]);

  const fetchClasses = useCallback(async () => {
    if (weekDates?.length === 0) return;
    setLoading(true);
    try {
      const params = { is_active: 'true' };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const { data } = await api.get('/classes', { params });
      setClasses(data || []);
    } catch (err) {
      showToast(err?.message || "Failed to load classes", "error");
    } finally {
      setLoading(false);
    }
  }, [weekDates, selectedBranchId]);

  const fetchTrainers = useCallback(async () => {
    try {
      const params = { role: 'trainer' };
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const { data } = await api.get('/staff', { params });
      setTrainers(data || []);
    } catch (err) {
      console.error("Failed to load trainers:", err);
    }
  }, [selectedBranchId]);

  useEffect(() => {
    fetchClasses();
    fetchTrainers();
  }, [fetchClasses, fetchTrainers]);

  const handleTrainerChange = async (classId, trainerId) => {
    try {
      await api.put(`/classes/${classId}`, { trainer_id: trainerId || null });
      await fetchClasses();
      showToast("Trainer updated successfully");
    } catch (err) {
      showToast(err?.message || "Failed to update trainer", "error");
    }
  };

  const goToPrevWeek = () => {
    const d = new Date(currentWeekBase);
    d?.setDate(d?.getDate() - 7);
    setCurrentWeekBase(d);
  };

  const goToNextWeek = () => {
    const d = new Date(currentWeekBase);
    d?.setDate(d?.getDate() + 7);
    setCurrentWeekBase(d);
  };

  const goToToday = () => setCurrentWeekBase(new Date());

  const today = new Date()?.toISOString()?.split("T")?.[0];

  // Trainer class count for header
  const trainerClassCounts = {};
  classes?.forEach(cls => {
    if (cls?.trainer_id) {
      trainerClassCounts[cls?.trainer_id] = (trainerClassCounts?.[cls?.trainer_id] || 0) + 1;
    }
  });

  const getTypeColor = (type) => CLASS_TYPE_COLORS?.[type] || CLASS_TYPE_COLORS?.General;

  return (
    <div className="space-y-4">
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-lg border shadow-lg text-sm font-medium ${
          toast?.type === "success" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"
        }`}>
          <Icon name={toast?.type === "success" ? "CheckCircle" : "XCircle"} size={16} />
          {toast?.message}
          <button onClick={() => setToast(null)} className="ml-2 opacity-60 hover:opacity-100">
            <Icon name="X" size={14} />
          </button>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold text-[var(--color-foreground)]">Class Schedule</h2>
          <p className="text-xs text-[var(--color-muted-foreground)]">Manage classes, enrollments, and attendance</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center bg-[var(--color-muted)] rounded-lg p-0.5">
            <button
              onClick={() => setViewMode("week")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === "week" ? "bg-[var(--color-card)] text-[var(--color-foreground)] shadow-sm" : "text-[var(--color-muted-foreground)]"}`}
            >
              <Icon name="CalendarDays" size={13} color="currentColor" />
              Week
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${viewMode === "list" ? "bg-[var(--color-card)] text-[var(--color-foreground)] shadow-sm" : "text-[var(--color-muted-foreground)]"}`}
            >
              <Icon name="List" size={13} color="currentColor" />
              List
            </button>
          </div>
          <button
            onClick={() => { setEditingClass(null); setShowAddModal(true); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
          >
            <Icon name="Plus" size={13} color="white" />
            Add Class
          </button>
        </div>
      </div>
      {/* Week Navigation */}
      <div className="flex items-center justify-between bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg px-4 py-2.5">
        <div className="flex items-center gap-2">
          <button onClick={goToPrevWeek} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="ChevronLeft" size={16} color="var(--color-foreground)" />
          </button>
          <button onClick={goToNextWeek} className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="ChevronRight" size={16} color="var(--color-foreground)" />
          </button>
          <span className="text-sm font-semibold text-[var(--color-foreground)]">
            {weekDates?.[0] && weekDates?.[6] && (
              `${formatDate(weekDates?.[0])} – ${formatDate(weekDates?.[6])}`
            )}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* Trainer class counts */}
          <div className="hidden md:flex items-center gap-2">
            {trainers?.slice(0, 3)?.map(t => (
              <div key={t?.id} className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
                <Icon name="User" size={11} color="currentColor" />
                <span>{t?.full_name?.split(" ")?.[0]}: {trainerClassCounts?.[t?.id] || 0}</span>
              </div>
            ))}
          </div>
          <button
            onClick={goToToday}
            className="px-3 py-1.5 text-xs font-medium border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors text-[var(--color-foreground)]"
          >
            Today
          </button>
        </div>
      </div>
      {/* Color Legend */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(CLASS_TYPE_COLORS)?.slice(0, 8)?.map(([type, colors]) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${colors?.dot}`} />
            <span className="text-xs text-[var(--color-muted-foreground)]">{type}</span>
          </div>
        ))}
      </div>
      {/* Week Grid View */}
      {viewMode === "week" && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="flex items-center gap-3 text-[var(--color-muted-foreground)]">
                <Icon name="Loader2" size={20} color="currentColor" className="animate-spin" />
                <span className="text-sm">Loading schedule...</span>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[700px]">
                {/* Day Headers */}
                <div className="grid grid-cols-8 border-b border-[var(--color-border)]">
                  <div className="px-3 py-2 text-xs text-[var(--color-muted-foreground)] font-medium border-r border-[var(--color-border)]">Time</div>
                  {weekDates?.map((date, i) => {
                    const dateStr = date?.toISOString()?.split("T")?.[0];
                    const isToday = dateStr === today;
                    return (
                      <div key={i} className={`px-2 py-2 text-center border-r border-[var(--color-border)] last:border-r-0 ${isToday ? "bg-[var(--color-primary)]/8" : ""}`}>
                        <p className={`text-xs font-semibold ${isToday ? "text-[var(--color-primary)]" : "text-[var(--color-foreground)]"}`}>{DAYS?.[i]}</p>
                        <p className={`text-xs ${isToday ? "text-[var(--color-primary)] font-bold" : "text-[var(--color-muted-foreground)]"}`}>{formatDate(date)}</p>
                      </div>
                    );
                  })}
                </div>

                {/* Time Slots */}
                {HOURS?.map(hour => (
                  <div key={hour} className="grid grid-cols-8 border-b border-[var(--color-border)] last:border-b-0 min-h-[60px]">
                    <div className="px-3 py-2 text-xs text-[var(--color-muted-foreground)] border-r border-[var(--color-border)] flex items-start pt-2 font-medium">
                      {formatHour(hour)}
                    </div>
                    {weekDates?.map((date, dayIdx) => {
                      const dateStr = date?.toISOString()?.split("T")?.[0];
                      const isToday = dateStr === today;
                      const slotClasses = getClassesForDayHour(classes, weekDates, dayIdx, hour);
                      return (
                        <div key={dayIdx} className={`px-1 py-1 border-r border-[var(--color-border)] last:border-r-0 space-y-1 ${isToday ? "bg-[var(--color-primary)]/4" : ""}`}>
                          {slotClasses?.map(cls => (
                            <ClassCard
                              key={cls?.id}
                              cls={cls}
                              trainers={trainers}
                              onEdit={(c) => { setEditingClass(c); setShowAddModal(true); }}
                              onEnroll={setEnrollmentClass}
                              onAttendance={setAttendanceClass}
                              onTrainerChange={handleTrainerChange}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {/* List View */}
      {viewMode === "list" && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4]?.map(i => (
                <div key={i} className="h-16 bg-[var(--color-muted)] rounded-lg animate-pulse" />
              ))}
            </div>
          ) : classes?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <div className="w-14 h-14 rounded-full bg-[var(--color-muted)] flex items-center justify-center mb-4">
                <Icon name="CalendarDays" size={24} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-base font-semibold text-[var(--color-foreground)]">No classes scheduled</p>
              <p className="text-sm text-[var(--color-muted-foreground)] mt-1 mb-4">Create your first class to get started</p>
              <button
                onClick={() => { setEditingClass(null); setShowAddModal(true); }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-secondary)] transition-colors"
              >
                <Icon name="Plus" size={15} color="white" />
                Add Class
              </button>
            </div>
          ) : (
            <div className="divide-y divide-[var(--color-border)]">
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-[var(--color-muted)]/40 text-xs font-semibold text-[var(--color-muted-foreground)]">
                <div className="col-span-3">Class</div>
                <div className="col-span-2">Schedule</div>
                <div className="col-span-2">Trainer</div>
                <div className="col-span-1">Enrolled</div>
                <div className="col-span-1">Attended</div>
                <div className="col-span-3 text-right">Actions</div>
              </div>
              {classes?.map(cls => {
                const typeColor = getTypeColor(cls?.class_type);
                const canMarkAttendance = isClassActive(cls);
                return (
                  <div key={cls?.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-[var(--color-muted)]/20 transition-colors">
                    <div className="col-span-3 flex items-center gap-2 min-w-0">
                      <div className={`w-2 h-8 rounded-full flex-shrink-0 ${typeColor?.dot}`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[var(--color-foreground)] truncate">{cls?.name}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${typeColor?.bg} ${typeColor?.text}`}>{cls?.class_type}</span>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-[var(--color-foreground)]">{cls?.start_date}</p>
                      <p className="text-xs text-[var(--color-muted-foreground)]">{cls?.start_time?.slice(0, 5)} – {cls?.end_time?.slice(0, 5)}</p>
                      <span className="text-xs text-[var(--color-muted-foreground)] capitalize">{cls?.recurrence?.replace("_", "-")}</span>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-[var(--color-foreground)] truncate">{cls?.trainer_name || "—"}</p>
                    </div>
                    <div className="col-span-1">
                      <span className={`text-xs font-medium ${cls?.enrolled_count >= cls?.max_capacity ? "text-red-500" : "text-[var(--color-foreground)]"}`}>
                        {cls?.enrolled_count}/{cls?.max_capacity}
                      </span>
                    </div>
                    <div className="col-span-1">
                      {cls?.attended_count > 0 ? (
                        <span className="text-xs font-medium text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                          {cls?.attended_count}
                        </span>
                      ) : (
                        <span className="text-xs text-[var(--color-muted-foreground)]">—</span>
                      )}
                    </div>
                    <div className="col-span-3 flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => setEnrollmentClass(cls)}
                        className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium border border-[var(--color-border)] rounded-md hover:bg-[var(--color-muted)] transition-colors text-[var(--color-foreground)]"
                      >
                        <Icon name="Users" size={11} color="currentColor" />
                        <span className="hidden sm:inline">Enroll</span>
                      </button>
                      {canMarkAttendance && (
                        <button
                          onClick={() => setAttendanceClass(cls)}
                          className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 transition-colors"
                        >
                          <Icon name="ClipboardCheck" size={11} color="currentColor" />
                          <span className="hidden sm:inline">Attend</span>
                        </button>
                      )}
                      <button
                        onClick={() => { setEditingClass(cls); setShowAddModal(true); }}
                        className="p-1.5 rounded-md hover:bg-[var(--color-muted)] transition-colors text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                      >
                        <Icon name="Pencil" size={13} color="currentColor" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {/* Modals */}
      {showAddModal && (
        <AddEditClassModal
          open={showAddModal}
          onClose={() => { setShowAddModal(false); setEditingClass(null); }}
          onSaved={() => { fetchClasses(); showToast(editingClass ? "Class updated!" : "Class created!"); }}
          editClass={editingClass}
        />
      )}
      {enrollmentClass && (
        <EnrollmentPanel
          classData={enrollmentClass}
          onClose={() => setEnrollmentClass(null)}
          onUpdated={fetchClasses}
        />
      )}
      {attendanceClass && (
        <AttendanceModal
          classData={attendanceClass}
          onClose={() => setAttendanceClass(null)}
          onSaved={() => { fetchClasses(); showToast("Attendance saved!"); }}
        />
      )}
    </div>
  );
}
